import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Vitals subsection adapter
 * Handles all vital signs measurements (8 fields)
 */
export class VitalsAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions for all vital signs
    const temperature = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_TEMPERATURE
    )?.at(0)

    const pulse = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_PULSE
    )?.at(0)

    const respirations = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_RESPIRATIONS
    )?.at(0)

    const o2Saturation = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_O2_SATURATION
    )?.at(0)

    const bloodPressure = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_PRESSURE
    )?.at(0)

    const height = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_HEIGHT
    )?.at(0)

    const bloodSugar = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_SUGAR
    )?.at(0)

    const weight = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_WEIGHT
    )?.at(0)

    return {
      [SocFormField.VITALS_TEMPERATURE]: temperature,
      [SocFormField.VITALS_PULSE]: pulse,
      [SocFormField.VITALS_RESPIRATIONS]: respirations,
      [SocFormField.VITALS_O2_SATURATION]: o2Saturation,
      [SocFormField.VITALS_BLOOD_PRESSURE]: bloodPressure,
      [SocFormField.VITALS_HEIGHT]: height,
      [SocFormField.VITALS_BLOOD_SUGAR]: bloodSugar,
      [SocFormField.VITALS_WEIGHT]: weight
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    return [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_TEMPERATURE,
        value: this.getFormDataField(SocFormField.VITALS_TEMPERATURE)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_PULSE,
        value: this.getFormDataField(SocFormField.VITALS_PULSE)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_RESPIRATIONS,
        value: this.getFormDataField(SocFormField.VITALS_RESPIRATIONS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_O2_SATURATION,
        value: this.getFormDataField(SocFormField.VITALS_O2_SATURATION)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_PRESSURE,
        value: this.getFormDataField(SocFormField.VITALS_BLOOD_PRESSURE)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_HEIGHT,
        value: this.getFormDataField(SocFormField.VITALS_HEIGHT)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_SUGAR,
        value: this.getFormDataField(SocFormField.VITALS_BLOOD_SUGAR)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_WEIGHT,
        value: this.getFormDataField(SocFormField.VITALS_WEIGHT)
      }
    ]
  }
}
